/**
 * ProShip Direct v2 — PACE v2 business knobs, merchant-facing.
 *
 * MIRRORED from KNOB_CATALOG in
 *   proship-backend/src/app/pages/AllocationConfig/decisionConfigService.js
 * which itself mirrors PACE's own GET /pace/v1/admin/knob-catalog — the source of truth. Keys,
 * defaults, units, bounds and the Advanced split are reproduced exactly; if PACE changes a default,
 * this file is wrong and the admin catalogue is right.
 *
 * WHAT IS DELIBERATELY NOT HERE: the `prism-iedd` group. Those knobs calibrate PRISM's delivery-date
 * promise, not allocation — PACE ignores prism_* keys — and the admin page filters them out the same
 * way (`group !== 'prism-iedd'`). They belong on an EDD screen, not this one.
 *
 * THE SCOPE MODEL, which shapes the whole screen: the real API writes to a scope, either `global`
 * or `merchant#<id>`, and a merchant value wins over the global one. So a merchant is never
 * "missing" a knob — every knob they have not set inherits ProShip's global default, and that is
 * the normal, working state. Same argument as serviceability and zones, and the screen says so
 * rather than showing blanks.
 *
 * THE AUDIT CONTRACT, likewise: the real PUT requires a reason on every change and appends to a
 * per-knob history (append-only, to proship_client_pace). A UI that let a merchant drag a slider
 * and walk away would not survive contact with that endpoint, so the reason field gates Save here
 * too and pending edits are held until it is filled.
 *
 * Business grouping is this module's own: the admin page lists twenty rows flat, which is right for
 * an operator who knows the model and wrong for a merchant who wants to answer "what do I want PACE
 * to care about". The keys and semantics are untouched.
 */

/** Knob groups, in the order a merchant should meet them. */
export const KNOB_GROUPS = [
  {
    key: 'profile',
    title: 'What to optimise for',
    blurb: 'The trade-off PACE makes when one courier is cheaper and another is faster.',
  },
  {
    key: 'scorecard',
    title: 'What matters most',
    blurb:
      'How much each thing counts when PACE compares couriers. Only the proportions matter, so each one is shown as its share of the score.',
  },
  {
    key: 'cost',
    title: 'What a failure costs you',
    blurb:
      'PACE prices risk in rupees before it ranks. Your numbers, not ours — a ₹1,200 parcel and a ₹12,000 parcel deserve different caution.',
  },
  {
    key: 'guardrail',
    title: 'Hard limits',
    blurb: 'Checked before anything is compared. A courier that fails one is not used at all.',
  },
  {
    key: 'unified',
    title: 'Scoring internals',
    blurb: 'Caps and normalisers.',
  },
];

/**
 * The one section where jargon is allowed.
 *
 * Everything the engine marks Advanced is a normaliser or a calibration internal — lambda scalers,
 * recency multipliers, support thresholds. A merchant cannot reason about them, and they are tuned
 * against ProShip-wide traffic rather than one merchant's lanes, so the honest treatment is one
 * collapsed section with a plain warning rather than nine rows sprinkled through the page looking
 * like ordinary settings.
 */
export const ADVANCED_SECTION = {
  title: 'Advanced — best left alone',
  blurb:
    'Internal calibration for the scoring maths. Tuned against ProShip-wide traffic rather than your lanes, and a wrong value here degrades allocation quietly rather than visibly. Change these only if ProShip support has asked you to.',
};

/**
 * The PACE knobs, exactly as the admin catalogue defines them.
 *
 * `label`/`description` are the catalogue's own text where it reads well to a merchant, reworded
 * where it assumed operator knowledge. `key`, `group`, `unit`, `default`, `min` and `max` are
 * verbatim — those are contract.
 */
export const PACE_KNOBS = [
  // ── profile ──
  {
    key: 'alpha',
    label: 'Cheaper or faster?',
    group: 'profile',
    unit: null,
    default: 0.5,
    min: 0,
    max: 1,
    step: 0.05,
    description: 'Where to land when one courier is cheaper and another is faster. The middle splits the difference.',
    ends: ['Save money', 'Save time'],
  },
  {
    key: 'lambda_fast',
    label: 'Speed normaliser (λ_fast)',
    group: 'profile',
    unit: null,
    default: 0.0138889,
    min: 0,
    max: null,
    advanced: true,
    description: 'Brings transit hours onto the blend scale (≈ 1 / typical hours).',
  },
  {
    key: 'lambda_cheap',
    label: 'Cost normaliser (λ_cheap)',
    group: 'profile',
    unit: null,
    default: 0.0111111,
    min: 0,
    max: null,
    advanced: true,
    description: 'Brings rupees onto the blend scale (≈ 1 / typical cost).',
  },

  // ── scorecard: the four weights a merchant actually reasons about ──
  {
    key: 'w_delivery',
    label: 'Delivery rate',
    group: 'scorecard',
    unit: null,
    default: 0.4,
    min: 0,
    max: null,
    step: 0.05,
    weight: true,
    description: 'How often the courier actually delivers, from your own shipment history.',
  },
  {
    key: 'w_fasr',
    label: 'First-attempt success',
    group: 'scorecard',
    unit: null,
    default: 0.2,
    min: 0,
    max: null,
    step: 0.05,
    weight: true,
    description: 'Delivered on the first attempt — fewer reattempts, fewer NDR calls.',
  },
  {
    key: 'w_tat',
    label: 'On-time discipline',
    group: 'scorecard',
    unit: null,
    default: 0.2,
    min: 0,
    max: null,
    step: 0.05,
    weight: true,
    description: 'First attempt landing inside the delivery date you promised the buyer.',
  },
  {
    key: 'w_cost',
    label: 'Cost and speed together',
    group: 'scorecard',
    unit: null,
    default: 0.2,
    min: 0,
    max: null,
    step: 0.05,
    weight: true,
    description: 'The logistics bucket — freight plus transit time, blended by the speed ↔ cost setting above.',
  },
  {
    key: 'recency_mult_7d',
    label: 'Recency weight — last 7 days',
    group: 'scorecard',
    unit: null,
    default: 1.5,
    min: 0,
    max: null,
    advanced: true,
    description: 'How much more the last week of performance counts than older history.',
  },
  {
    key: 'recency_mult_30d',
    label: 'Recency weight — 8 to 30 days',
    group: 'scorecard',
    unit: null,
    default: 1.25,
    min: 0,
    max: null,
    advanced: true,
    description: 'Multiplier on shipments in the 8–30 day bucket.',
  },
  {
    key: 'recency_mult_90d',
    label: 'Recency weight — 31 to 90 days',
    group: 'scorecard',
    unit: null,
    default: 1.0,
    min: 0,
    max: null,
    advanced: true,
    description: 'Multiplier on shipments in the 31–90 day bucket.',
  },
  {
    key: 'scorecard_support_min',
    label: 'Minimum history to trust a lane',
    group: 'scorecard',
    unit: 'shipments',
    default: 30,
    min: 1,
    max: null,
    advanced: true,
    description:
      'Recency-weighted shipments needed before PACE trusts a courier’s record on a specific lane rather than falling back to its state or overall record.',
  },

  // ── cost ──
  {
    key: 'rto_cost',
    label: 'Cost of an RTO',
    group: 'cost',
    unit: 'INR',
    default: 180,
    min: 0,
    max: null,
    step: 10,
    description: 'What one return-to-origin costs you all in. Priced as this × the chance of RTO.',
  },
  {
    key: 'lost_cost',
    label: 'Cost of a lost or damaged parcel',
    group: 'cost',
    unit: 'INR',
    default: 1200,
    min: 0,
    max: null,
    step: 50,
    description: 'Replacement plus goodwill, not just freight. Priced as this × the loss rate.',
  },
  {
    key: 'reattempt_cost',
    label: 'Cost of a failed first attempt',
    group: 'cost',
    unit: 'INR',
    default: 45,
    min: 0,
    max: null,
    step: 5,
    description: 'What a second attempt costs you. Priced as this × the chance the first one fails.',
  },
  {
    key: 'risk_weight',
    label: 'Count risk as cost',
    group: 'cost',
    unit: null,
    default: 0,
    min: 0,
    // The engine sets no ceiling here — above 1 over-weights risk beyond its priced value, which
    // is unusual but legal. So the contract stays open and only the slider is bounded: validation
    // must never reject a value PACE would accept.
    max: null,
    sliderMax: 1,
    step: 0.1,
    description:
      'Off, couriers are compared on freight alone. Full, the failure costs above are added to each price — so a cheap courier that loses parcels stops looking cheap.',
    ends: ['Off - freight only', 'Full - add failure costs'],
  },
  {
    key: 'surcharge_weight',
    label: 'Weight-discrepancy surcharge multiplier',
    group: 'cost',
    unit: null,
    default: 1,
    min: 0,
    max: null,
    advanced: true,
    description: 'Multiplier on the expected weight-discrepancy surcharge.',
  },

  // ── guardrail ──
  {
    key: 'min_delivery_prob',
    label: 'Skip couriers unlikely to deliver',
    group: 'guardrail',
    unit: 'probability',
    default: 0.5,
    min: 0,
    max: 1,
    step: 0.05,
    description:
      'A courier less likely than this to deliver on the lane is not used. Raise it and you ship with fewer, safer couriers; set it to zero and nothing is excluded.',
  },
  {
    key: 'lost_rate_default',
    label: 'Assumed loss rate for new couriers',
    group: 'guardrail',
    unit: 'probability',
    default: 0.002,
    min: 0,
    max: 1,
    advanced: true,
    description: 'Stand-in chance of loss until a courier has enough of its own history.',
  },

  // ── unified ──
  {
    key: 'logistics_cap',
    label: 'Logistics cap',
    group: 'unified',
    unit: null,
    default: 2,
    min: 0,
    max: null,
    advanced: true,
    description:
      'Stops the cost-and-speed penalty swamping the performance weights on an expensive or slow lane.',
  },
];

/** The four weights that normalise against each other. */
export const SCORECARD_WEIGHT_KEYS = PACE_KNOBS.filter((k) => k.weight).map((k) => k.key);

export const findKnob = (key) => PACE_KNOBS.find((k) => k.key === key) || null;

export const knobsInGroup = (group, advanced) =>
  PACE_KNOBS.filter((k) => k.group === group && Boolean(k.advanced) === advanced);

/** Groups a merchant sees up front: those with at least one knob they can reason about. */
export const BASIC_GROUPS = KNOB_GROUPS.filter((g) => knobsInGroup(g.key, false).length > 0);

/** Everything the engine marks Advanced, in one list, whatever group it belongs to. */
export const ADVANCED_KNOBS = PACE_KNOBS.filter((k) => k.advanced);

/**
 * Allocation profiles PACE accepts.
 *
 * The profile decides which knobs bite: `cheapest` and `fastest` ignore the blend and the
 * scorecard entirely, `balanced` uses the blend, and `weighted` is the one that reads the four
 * performance weights. Choosing a profile and then tuning weights it ignores is the easiest
 * mistake to make on this screen, so it is called out where the profile is chosen.
 */
export const PACE_PROFILES = [
  {
    value: 'weighted',
    label: 'Best overall',
    blurb: 'Judges couriers on how they have actually performed for you, then on cost and speed.',
    uses: ['profile', 'scorecard', 'cost', 'guardrail'],
    recommended: true,
  },
  {
    value: 'balanced',
    label: 'Cost and speed',
    blurb: 'Balances price against transit time. Past performance is not taken into account.',
    uses: ['profile', 'cost', 'guardrail'],
  },
  {
    value: 'cheapest',
    label: 'Cheapest',
    blurb: 'Always the lowest cost, whatever its record.',
    uses: ['cost', 'guardrail'],
  },
  {
    value: 'fastest',
    label: 'Fastest',
    blurb: 'Always the quickest, whatever it costs.',
    uses: ['guardrail'],
  },
];

/**
 * What ProShip recommends, and how to tell whether a merchant is on it.
 *
 * The recommendation is not a separate set of numbers: ProShip's recommended values ARE the global
 * defaults in this catalogue, tuned against ProShip-wide traffic. So applying it clears the
 * merchant's overrides rather than writing a second copy of the same numbers — which also means it
 * cannot drift out of step with what ProShip actually recommends.
 */
export const RECOMMENDED = {
  profile: 'weighted',
  title: 'ProShip recommended',
  blurb:
    'Balanced performance: couriers judged on their record with you, with cost and speed weighted evenly behind delivery reliability. Tuned on ProShip-wide traffic across every lane.',
};

export const isOnRecommended = (profile, overrides) =>
  profile === RECOMMENDED.profile && Object.keys(overrides || {}).length === 0;

export const findProfile = (value) => PACE_PROFILES.find((p) => p.value === value) || PACE_PROFILES[3];

/** How a value is written for a merchant, given its unit. */
export function formatKnob(knob, value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  if (knob.unit === 'INR') return `₹${n.toLocaleString('en-IN')}`;
  if (knob.unit === 'probability') return `${Math.round(n * 100)}%`;
  if (knob.unit === 'shipments') return `${n.toLocaleString('en-IN')} shipments`;
  return String(Number(n.toFixed(7)));
}

/** The top of the slider: an explicit UI bound where the contract leaves the range open. */
export const sliderCeiling = (knob) => (knob.sliderMax !== undefined ? knob.sliderMax : knob.max);

/** Whether a proposed value is inside the catalogue's bounds. */
export function knobError(knob, value) {
  if (value === '' || value === null || value === undefined) return 'Enter a value, or reset to the ProShip default.';
  const n = Number(value);
  if (!Number.isFinite(n)) return 'Numbers only.';
  if (knob.min !== null && knob.min !== undefined && n < knob.min) return `Cannot be below ${knob.min}.`;
  if (knob.max !== null && knob.max !== undefined && n > knob.max) return `Cannot be above ${knob.max}.`;
  return null;
}
